import {
  RiEdit2Fill,
  RiSendPlaneLine,
  RiEyeLine,
  RiFlightTakeoffLine,
  RiCheckboxCircleFill,
  RiHourglass2Line,
  RiCloseCircleLine,
  RiCloseCircleFill,
  RiPenNibLine,
  RiInformationFill
} from '@remixicon/react';

export const quoteStatusConfig = {
  'DRAFT': { label: 'Draft', className: 'bg-gray-100 text-gray-600', icon: RiEdit2Fill },
  'PENDING_APPROVAL': { label: 'Pending Approval', className: 'bg-amberlight text-amberdark', icon: RiHourglass2Line },
  'SENT': { label: 'Sent', className: 'bg-blue-50 text-blue-600', icon: RiSendPlaneLine },
  'VIEWED': { label: 'Seen', className: 'bg-blue-50 text-blue-600',  icon: RiEyeLine },
  'SIGNED': { label: 'Signed', className: 'bg-blue-50 text-blue-600', icon: RiPenNibLine },
  'IN_TRANSIT': { label: 'In Transit', className: 'bg-amber-50 text-amber-600', icon: RiFlightTakeoffLine },
  'COMPLETED': { label: 'Completed', className: 'bg-purple-50 text-purple-600', icon: RiCheckboxCircleFill },
  'ACCEPTED': { label: 'Confirmed', className: 'bg-green-150 text-green-450', icon: RiCheckboxCircleFill },
  'CAPTURED': { label: 'Paid', className: 'bg-green-150 text-green-450', icon: RiCheckboxCircleFill },
  'SETTLED_VIA_PASSPORT':  { label: 'Paid', className: 'bg-green-150 text-green-450', icon: RiCheckboxCircleFill },
  'TRIPSHEET_PENDING_APPROVAL': { label: 'Trip Sheet Pending Approval', className: 'bg-amberlight text-amberdark', icon: RiHourglass2Line },
  'PASSENGERS_PENDING': { label: 'Pending Passengers', className: 'bg-amberlight text-amberdark', icon: RiHourglass2Line },
  // Virtual status — derived FE-side by getEffectiveQuoteStatus below when
  // the raw quote status is PASSENGERS_PENDING but the client has already
  // submitted (files.request_client_passengers === 2 OR passengers.length > 0).
  // Not a BE-emitted status; never appears in `quote.status` on its own.
  // Uses an emerald green — reads as "positive progress" (the client did
  // their part) while staying distinct from the ACCEPTED/CAPTURED chip
  // that uses a different green for the "Paid" state.
  'TRIP_SHEET_IN_PROGRESS': { label: 'Trip Sheet In Progress', className: 'bg-emerald-50 text-emerald-700', icon: RiCheckboxCircleFill },
  'WIRE_TRANSFER_IN_PROGRESS': { label: 'Wire Transfer In-Progress', className: 'bg-amberlight text-amberdark', icon: RiHourglass2Line },
  'PAYMENT_PROCESSING': { label: 'Payment Processing', className: 'bg-amberlight text-amberdark', icon: RiHourglass2Line },
  'WAITING_FLIGHT': { label: 'Waiting Flight', className: 'bg-amber-50 text-amber-600', icon: RiHourglass2Line },
  'DECLINED': { label: 'Declined', className: 'bg-red-50 text-red-600', icon: RiCloseCircleLine },
  'REJECTED': { label: 'Cancelled', className: 'bg-red-50 text-red-600', icon: RiCloseCircleLine },
  'PENDING_PAYMENT': { label: 'Pending Payment', className: 'bg-amberlight text-amberdark', icon: RiHourglass2Line },
  'PRE_AUTHORIZED': { label: 'Payment Authorized', className: 'bg-blue-50 text-blue-600', icon: RiInformationFill },
  'VOID': { label: 'Invoice Cancelled', className: 'bg-red-50 text-red-600', icon: RiCloseCircleFill },
  'CANCELLED': { label: 'Trip Cancelled', className: 'bg-red-50 text-red-600', icon: RiCloseCircleFill }
};

// Unmapped statuses fall back to a title-cased label
// (e.g. "SOME_NEW_STATUS" → "Some New Status") so they never render raw.
const titleCaseStatus = (status) =>
  status
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const getQuoteStatusConfig = (status) =>
  quoteStatusConfig[status] || {
    label: status ? titleCaseStatus(status) : 'Draft',
    className: 'bg-gray-100 text-gray-600',
    icon: RiEdit2Fill,
  };

/**
 * Derive the effective (display) status for a quote. Currently promotes
 * PASSENGERS_PENDING → TRIP_SHEET_IN_PROGRESS once the client has
 * submitted passenger details (either `files.request_client_passengers`
 * has been flipped to 2 by ClientTripSheet.jsx, or the quote already
 * has passengers loaded on it). Every other status passes through
 * unchanged.
 *
 * Second arg lets callers with an already-resolved display status
 * (e.g. QuoteCard's mix of payment_status + quote status) route it
 * through this helper without having to mutate the quote object:
 *   getQuoteStatusConfig(getEffectiveQuoteStatus(quote, myResolvedStatus))
 *
 * This is a FE-side derivation only; BE-emitted `quote.status` stays
 * PASSENGERS_PENDING. If BE ever adds a real "next step" transition
 * this helper can shrink to a no-op.
 */
export const getEffectiveQuoteStatus = (quote, status) => {
  const raw = status !== undefined ? status : quote?.status;
  if (!raw) return raw;
  if (raw === 'PASSENGERS_PENDING') {
    // BE serializes files.request_client_passengers inconsistently — the
    // notification-event payload had it as the string "2", while writes
    // from ClientTripSheet.jsx send number 2. Coerce to string to
    // handle both without eslint hitting on ==.
    const flagRaw = quote?.files?.request_client_passengers;
    const requested = flagRaw != null && String(flagRaw) === '2';
    const hasPassengers = Array.isArray(quote?.passengers) && quote.passengers.length > 0;
    if (requested || hasPassengers) return 'TRIP_SHEET_IN_PROGRESS';
  }
  return raw;
};
